/** App shell (spec §10.3): sidebar on desktop, bottom bar under 768 px, one-line header. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigationType } from 'react-router'
import { PickerStyleContext } from '@/lib/pickerStyle'
import { usePickerStyle, useThemePreference } from '@/lib/useThemePreference'
import { t } from '@/lib/text'
import { CommandPalette } from '../CommandPalette'
import { HelpSheet, type HelpTab } from '../HelpSheet'
import { RequirePermission } from '../guards'
import { BottomBar } from './BottomBar'
import { Header } from './Header'
import { ShellContext } from './ShellContext'
import { Sidebar } from './Sidebar'
import { useScreenTitle } from './useScreenTitle'

const COLLAPSED_KEY = 'crm.sidebar.collapsed'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  return !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))
}

/** A newly opened page starts at its top; Back and Forward keep the browser's own position, and changing
 * filters on the same page (only the query string) stays put. */
function useStartAtTop() {
  const { pathname } = useLocation()
  const navigationType = useNavigationType()
  const previous = useRef(pathname)
  useEffect(() => {
    if (previous.current === pathname) return
    previous.current = pathname
    if (navigationType !== 'POP') window.scrollTo(0, 0)
  }, [pathname, navigationType])
}

export function AppShell() {
  useScreenTitle()
  useStartAtTop()
  const pickerStyle = usePickerStyle().choice
  useThemePreference()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [helpTab, setHelpTab] = useState<HelpTab | null>(null)
  const [collapsed, setCollapsed] = useState(readCollapsed)

  const toggleSidebar = useCallback(() => {
    setCollapsed((value) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, value ? '0' : '1')
      } catch {
        // Storage blocked: the toggle still works for this page.
      }
      return !value
    })
  }, [])

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setPaletteOpen(true)
      } else if (event.key === '?' && event.shiftKey && !isTyping(event.target)) {
        event.preventDefault()
        setHelpTab('shortcuts')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const controls = useMemo(
    () => ({ openPalette: () => setPaletteOpen(true), openHelp: (tab: HelpTab = 'help') => setHelpTab(tab) }),
    [],
  )

  return (
    <ShellContext.Provider value={controls}>
      <PickerStyleContext.Provider value={pickerStyle}>
      <a
        href="#content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[70] focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:ring-[3px] focus:ring-ring/50"
      >
        {t('core.shell.skip')}
      </a>
      <div className="flex min-h-dvh">
        <Sidebar collapsed={collapsed} />
        <div className="flex min-w-0 flex-1 flex-col">
          <Header onToggleSidebar={toggleSidebar} />
          <main id="content" tabIndex={-1} className="flex-1 px-4 py-4 pb-24 focus:outline-none md:px-6 md:pb-6">
            <RequirePermission>
              <Outlet />
            </RequirePermission>
          </main>
        </div>
      </div>
      <BottomBar />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <HelpSheet tab={helpTab} onTabChange={setHelpTab} />
      </PickerStyleContext.Provider>
    </ShellContext.Provider>
  )
}
